import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Download, X } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexActaCalificaciones() {
    const [filters, setFilters] = useState({
        id_carrera: '',
        id_mencion: '',
        id_asignatura: '',
        id_paralelo: '',
        gestion: new Date().getFullYear()
    });

    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [asignaturas, setAsignaturas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [notas, setNotas] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');

    const [showDownloadModal, setShowDownloadModal] = useState(false);

    // Periodos (1P, 2P, 3P, 4P)
    const periodos = [1, 2, 3, 4];

    useEffect(() => { 
        loadCarreras(); 
        loadAsignaturas(); 
        verificarPeriodoCargadoNotas();
    }, []);

    useEffect(() => {
        if (filters.id_carrera) {
            loadMenciones(filters.id_carrera);
            loadAsignaturas(filters.id_carrera, null);
        } else {
            setMenciones([]);
            loadAsignaturas(null, null);
        }
    }, [filters.id_carrera]);

    useEffect(() => {
        if (filters.id_mencion) {
            loadAsignaturas(filters.id_carrera, filters.id_mencion);
        }
    }, [filters.id_mencion]);

    useEffect(() => {
        if (filters.id_asignatura) loadParalelos(filters.id_asignatura);
        else setParalelos([]);
    }, [filters.id_asignatura]);

    useEffect(() => {
        if (filters.id_asignatura && filters.id_paralelo) loadEstudiantes();
    }, [filters.id_asignatura, filters.id_paralelo, filters.gestion]);

    const verificarPeriodoCargadoNotas = async () => {
        try {
            const paramName = 'Cargado de notas Licenciatura > Acta Calificaciones (docente)';
            const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent(paramName)}`);
            const data = await response.json();
            
            if (!data.habilitado) {
                setInscripcionCerrada(true);
                setMensajeCierre(data.mensaje || 'El periodo de cargado de actas de calificaciones se encuentra cerrado.');
            } else {
                setInscripcionCerrada(false);
            }
        } catch (error) {
            console.error('Error verificando periodo:', error);
        }
    };

    const loadCarreras = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/carreras`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setCarreras(Array.isArray(data.carreras) ? data.carreras : []);
        } catch (e) { console.error('Error:', e); setCarreras([]); }
    };

    const loadMenciones = async (id_carrera) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/menciones/${id_carrera}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setMenciones(Array.isArray(data.menciones) ? data.menciones : []);
        } catch (e) { console.error('Error:', e); setMenciones([]); }
    };

    const loadAsignaturas = async (carrera, mencion) => {
        try {
            const token = localStorage.getItem('token');
            const query = new URLSearchParams();
            if (carrera) query.append('carrera', carrera);
            if (mencion) query.append('mencion', mencion);
            query.append('gestion', filters.gestion);
            const res = await fetch(`${API_URL}/docente/asignaturas?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setAsignaturas(Array.isArray(data.asignaturas) ? data.asignaturas : []);
        } catch (e) { console.error('Error:', e); setAsignaturas([]); }
    };

    const loadParalelos = async (id_asignatura) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}?gestion=${filters.gestion}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setParalelos(Array.isArray(data.paralelos) ? data.paralelos : []);
        } catch (e) { console.error('Error:', e); setParalelos([]); }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion,
                tipo: 'matriculacion'
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setEstudiantes(data.estudiantes || []);
            await loadNotas();
        } catch (e) { console.error('Error:', e); }
        finally { setLoading(false); }
    };

    const loadNotas = async () => {
        try {
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion,
                tipo: 'consolidado'
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/notas-consolidado?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();

            const map = {};
            (data.notas || []).forEach(n => {
                map[`${n.id_estudiante}_${n.campo}`] = n.valor !== null && n.valor !== undefined && n.valor !== '' ? n.valor : '';
            });
            setNotas(map);
        } catch (e) { console.error('Error loadNotas Acta:', e); }
    };

    const getNota = (id_estudiante, campo) => {
        const val = notas[`${id_estudiante}_${campo}`];
        return (val !== undefined && val !== null && val !== '') ? val : '-';
    };

    const handleNotaChange = (id_estudiante, campo, valor) => {
        const numVal = parseFloat(valor);
        if (valor !== '' && (isNaN(numVal) || numVal < 0 || numVal > 100)) return;
        setNotas(prev => ({ ...prev, [`${id_estudiante}_${campo}`]: valor }));
    };

    const handleGuardar = async () => {
        try {
            setSaving(true);
            const gestion = filters.gestion || new Date().getFullYear();
            const entries = [];

            const asignaturaNombre = asignaturas.find(a => String(a.id_asignatura) === String(filters.id_asignatura))?.nombre || 'Asignatura';

            estudiantes.forEach(est => {
                const t2 = getNota(est.id_estudiante, '2T');
                const nf = getFinalCalculado(est.id_estudiante);

                entries.push({
                    id_estudiante: est.id_estudiante,
                    id_asignatura: filters.id_asignatura,
                    id_paralelo: filters.id_paralelo,
                    campo: 'NF_ACTA',
                    valor: parseFloat(nf) || 0,
                    gestion,
                    nota_final: parseFloat(nf) || 0,
                    nombre_asignatura: asignaturaNombre
                });

                if (t2 !== '-' && t2 !== '' && t2 !== null) {
                    entries.push({
                        id_estudiante: est.id_estudiante,
                        id_asignatura: filters.id_asignatura,
                        id_paralelo: filters.id_paralelo,
                        campo: '2T',
                        valor: parseFloat(t2),
                        gestion,
                        nota_final: parseFloat(nf),
                        nombre_asignatura: asignaturaNombre
                    });
                }
            });

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/notas-consolidado/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ notas: entries })
            });

            if (!res.ok) throw new Error('Error al guardar');

            alert('Acta de Calificaciones guardada exitosamente');
            await loadNotas();
        } catch (e) { console.error('Error:', e); alert('Error al guardar'); }
        finally { setSaving(false); }
    };

    const getNotaFinal = (id_estudiante) => {
        let sum = 0;
        periodos.forEach(p => {
            const val = parseFloat(getNota(id_estudiante, `P${p}`)) || 0;
            sum += val;
        });
        return Math.round(sum / 4);
    };

    const getFinalCalculado = (id_estudiante) => {
        const nf = parseFloat(getNotaFinal(id_estudiante)) || 0;
        const t2Value = getNota(id_estudiante, '2T');
        const t2 = (t2Value !== '-' && t2Value !== '') ? parseFloat(t2Value) : 0;

        if (t2 > 0) {
            return t2 >= 61 ? 61 : t2;
        }
        return nf;
    };

    const getEstado = (id_estudiante) => {
        const nota = getFinalCalculado(id_estudiante);
        if (nota === 0 && getNotaFinal(id_estudiante) === 0) return '-';
        return nota >= 61 ? 'Aprobado' : 'Reprobado';
    };

    const numeroALetras = (num) => {
        if (num === '-' || isNaN(num)) return '-';
        const n = Math.round(parseFloat(num));
        const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];

        if (n === 0) return 'CERO';
        if (n === 100) return 'CIEN';

        let letras = '';
        if (n >= 10 && n < 20) {
            letras = especiales[n - 10];
        } else if (n >= 20 && n < 30) {
            const u = n % 10;
            if (u === 0) letras = 'VEINTE';
            else if (u === 1) letras = 'VEINTIUNO';
            else letras = 'VEINTI' + unidades[u];
        } else {
            const d = Math.floor(n / 10);
            const u = n % 10;
            letras = decenas[d];
            if (d > 0 && u > 0) letras += ' Y ' + unidades[u];
            else if (d === 0 && u > 0) letras = unidades[u];
        }
        return letras;
    };

    const handleDownload = async () => {
        try {
            const token = localStorage.getItem('token');
            const query = new URLSearchParams({
                tipo: 'consolidado',
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion
            });
            const res = await fetch(`${API_URL}/docente/notas-consolidado/export?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Error al generar Excel');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `acta_calificaciones_${filters.id_asignatura}_${filters.gestion}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setShowDownloadModal(false);
        } catch (e) { alert('Error al descargar'); }
    };

    const filteredEstudiantes = estudiantes.filter(est => {
        const s = searchTerm.toLowerCase();
        return (est.codigo || '').toLowerCase().includes(s) ||
            (est.nombres || '').toLowerCase().includes(s) ||
            (est.apellidos || '').toLowerCase().includes(s);
    });

    return (
        <div className="p-4 md:p-6 max-w-full mx-auto">
            <h1 className="text-xl font-bold text-gray-800 mb-6">Kardex &gt; Calificaciones &gt; Acta de Calificaciones</h1>

            <div className="flex flex-wrap gap-2 mb-6">
                <div className="relative">
                    <select value={filters.gestion} onChange={(e) => setFilters({ ...filters, gestion: parseInt(e.target.value) })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px]">
                        {[2024, 2025, 2026, 2027].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_carrera} onChange={(e) => setFilters({ ...filters, id_carrera: e.target.value, id_mencion: '', id_asignatura: '', id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px]">
                        <option value="">Carrera</option>
                        {carreras.map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_mencion} onChange={(e) => setFilters({ ...filters, id_mencion: e.target.value, id_asignatura: '', id_paralelo: '' })}
                        disabled={!filters.id_carrera} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px] disabled:bg-gray-50">
                        <option value="">Mención</option>
                        {menciones.map(m => <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_asignatura} onChange={(e) => setFilters({ ...filters, id_asignatura: e.target.value, id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[200px]">
                        <option value="">Asignatura</option>
                        {asignaturas.map(a => <option key={a.id_asignatura} value={a.id_asignatura}>{a.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.id_paralelo} onChange={(e) => setFilters({ ...filters, id_paralelo: e.target.value })}
                        disabled={!filters.id_asignatura} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px] disabled:bg-gray-100">
                        <option value="">Paralelo</option>
                        {paralelos.map(p => <option key={p.id_paralelo || p.paralelo} value={p.id_paralelo || p.paralelo}>{p.paralelo || p.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative flex-1 max-w-xs">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm" />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>

                <button onClick={() => setShowDownloadModal(true)} className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700" title="Exportar Reporte">
                    <Download size={18} />
                </button>
            </div>

            {/* Mensaje de Periodo Cerrado */}
            {inscripcionCerrada && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium">{mensajeCierre}</p>
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-3 py-3 border text-center font-semibold">No</th>
                            <th className="px-3 py-3 border text-center font-semibold">Código</th>
                            <th className="px-3 py-3 border text-center font-semibold">Nombres</th>
                            <th className="px-3 py-3 border text-center font-semibold">Apellidos</th>
                            <th className="px-3 py-3 border text-center font-semibold">1P</th>
                            <th className="px-3 py-3 border text-center font-semibold">2P</th>
                            <th className="px-3 py-3 border text-center font-semibold">3P</th>
                            <th className="px-3 py-3 border text-center font-semibold">4P</th>
                            <th className="px-3 py-3 border text-center font-semibold">NF</th>
                            <th className="px-3 py-3 border text-center font-semibold">2T</th>
                            <th className="px-3 py-3 border text-center font-semibold">Nota literal</th>
                            <th className="px-3 py-3 border text-center font-semibold">Estado</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr><td colSpan={12} className="py-8 text-center text-gray-500">Cargando...</td></tr>
                        ) : filteredEstudiantes.length === 0 ? (
                            <tr><td colSpan={12} className="py-8 text-center text-gray-500">
                                {filters.id_paralelo ? 'No hay estudiantes' : 'Seleccione filtros'}
                            </td></tr>
                        ) : filteredEstudiantes.map((est, i) => (
                            <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                <td className="px-3 py-2 border text-center">{i + 1}</td>
                                <td className="px-3 py-2 border text-center">{est.codigo || '-'}</td>
                                <td className="px-3 py-2 border text-center font-medium">{est.nombres || '-'}</td>
                                <td className="px-3 py-2 border text-center font-medium">{est.apellidos || '-'}</td>
                                {periodos.map(p => (
                                    <td key={p} className="px-3 py-2 border text-center">{getNota(est.id_estudiante, `P${p}`)}</td>
                                ))}
                                <td className={`px-3 py-2 border text-center font-bold ${getNota(est.id_estudiante, '2T') !== '-' && getNota(est.id_estudiante, '2T') !== '' ? 'opacity-30' : ''}`}>
                                    {getNotaFinal(est.id_estudiante) || '-'}
                                </td>
                                <td className="px-3 py-2 border text-center bg-yellow-50/30">
                                    <input type="number" min="0" max="100" value={getNota(est.id_estudiante, '2T') !== '-' ? getNota(est.id_estudiante, '2T') : ''}
                                        onChange={(e) => handleNotaChange(est.id_estudiante, '2T', e.target.value)}
                                        className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-xs" />
                                </td>
                                <td className="px-3 py-2 border text-center text-[10px] font-semibold text-blue-800">
                                    {numeroALetras(getFinalCalculado(est.id_estudiante))}
                                </td>
                                <td className={`px-3 py-2 border text-center font-bold text-xs ${getEstado(est.id_estudiante) === 'Aprobado' ? 'text-green-600' :
                                    getEstado(est.id_estudiante) === 'Reprobado' ? 'text-red-600' : ''
                                    }`}>
                                    {getEstado(est.id_estudiante)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredEstudiantes.length > 0 && (
                    <div className="p-4 border-t flex justify-end">
                        <button onClick={handleGuardar} disabled={saving}
                            className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 disabled:opacity-50">
                            {saving ? 'Guardando...' : 'Guardar Notas'}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal Descarga - Acta */}
            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Descargar Acta de Calificaciones</h2>
                                <p className="text-xs text-gray-500">Gestión seleccionada: {filters.gestion}</p>
                            </div>
                            <button onClick={() => setShowDownloadModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Download size={32} />
                            </div>
                            <h3 className="font-medium text-gray-900 mb-1">Generar Acta Consolidada</h3>
                            <p className="text-sm text-gray-500">
                                Este reporte consolidará todos los periodos académicos y la nota final de la asignatura seleccionada.
                            </p>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t gap-3 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setShowDownloadModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition">
                                Cancelar
                            </button>
                            <button onClick={handleDownload}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md font-medium hover:bg-blue-800 transition shadow-md">
                                Descargar Reporte Final
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
